import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// steps = [ { targetId: 'dashboard-card', content: 'This is your dashboard', position: 'bottom' }, ... ]
const TutorialOverlay = ({ steps, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    const updateRect = () => {
      if (steps.length === 0 || currentStep >= steps.length) return;
      const targetElement = document.getElementById(steps[currentStep].targetId);
      if (targetElement) {
        setTargetRect(targetElement.getBoundingClientRect());
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // If element not found, just clear rect
        setTargetRect(null);
      }
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    // Simple polling just in case elements render slightly late
    const interval = setInterval(updateRect, 500);
    
    return () => {
      window.removeEventListener('resize', updateRect);
      clearInterval(interval);
    };
  }, [currentStep, steps]);

  if (steps.length === 0 || currentStep >= steps.length) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const currentStepData = steps[currentStep];

  // Calculate tooltip position based on targetRect
  let tooltipStyle = {};
  if (targetRect) {
    const margin = 16;
    if (currentStepData.position === 'bottom') {
      tooltipStyle = { top: targetRect.bottom + margin, left: targetRect.left + (targetRect.width / 2) };
    } else if (currentStepData.position === 'top') {
      tooltipStyle = { top: targetRect.top - margin, left: targetRect.left + (targetRect.width / 2), transform: 'translate(-50%, -100%)' };
    } else if (currentStepData.position === 'right') {
      tooltipStyle = { top: targetRect.top + (targetRect.height / 2), left: targetRect.right + margin, transform: 'translate(0, -50%)' };
    } else if (currentStepData.position === 'left') {
      tooltipStyle = { top: targetRect.top + (targetRect.height / 2), left: targetRect.left - margin, transform: 'translate(-100%, -50%)' };
    } else {
      tooltipStyle = { top: targetRect.bottom + margin, left: targetRect.left + (targetRect.width / 2) };
    }
    if (currentStepData.position === 'bottom') {
       tooltipStyle.transform = 'translate(-50%, 0)';
    }
  } else {
    // Center fallback if element not found
    tooltipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      <div className="absolute inset-0 bg-black/60 pointer-events-auto transition-opacity" />
      
      {/* Highlighting target */}
      {targetRect && (
        <div 
          className="absolute border-4 border-white rounded-lg pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] z-[101] transition-all duration-300"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)'
          }}
        />
      )}

      {/* Tooltip Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="absolute z-[102] bg-white rounded-xl shadow-2xl p-6 pointer-events-auto w-80 text-gray-800"
          style={tooltipStyle}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg text-purple-600">Tutorial ({currentStep + 1}/{steps.length})</h3>
            <button onClick={handleSkip} className="text-gray-400 hover:text-gray-600 text-sm font-semibold">Skip</button>
          </div>
          
          <p className="text-sm text-gray-600 mb-6">{currentStepData.content}</p>
          
          <div className="flex justify-end">
            <button 
              onClick={handleNext}
              className="px-5 py-2 bg-purple-600 text-white rounded-lg font-bold shadow-md hover:bg-purple-700 transition-colors"
            >
              {currentStep < steps.length - 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default TutorialOverlay;
